import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import {
  listScans,
  listOpsPackages,
  listOpsEmergency,
  type VisitorScan,
  type PublicPackage,
  type PublicEmergencyAlert,
} from '../../lib/ops-api';
import { extractApiError } from '../../lib/api-client';
import { useT } from '../../i18n';

function fmt(iso: string): string {
  try {
    return new Date(iso).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export function OperationsPage() {
  const t = useT();
  const [tab, setTab] = useState(0);
  return (
    <Stack spacing={3}>
      <div>
        <Typography variant="h4" fontWeight={600}>
          {t('ops.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('ops.subtitle')}
        </Typography>
      </div>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label={t('ops.tabVisitors')} />
        <Tab label={t('ops.tabPackages')} />
        <Tab label={t('ops.tabEmergency')} />
      </Tabs>

      {tab === 0 ? <VisitorsLog /> : tab === 1 ? <PackagesLog /> : <EmergencyLog />}
    </Stack>
  );
}

function LogShell<T>({
  load,
  empty,
  head,
  row,
}: {
  load: () => Promise<T[]>;
  empty: string;
  head: React.ReactNode;
  row: (item: T) => React.ReactNode;
}) {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    load()
      .then((r) => {
        if (alive) setItems(r);
      })
      .catch((e) => {
        if (alive) setError(extractApiError(e).message);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) return <Alert severity="error">{error}</Alert>;
  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">{empty}</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>{head}</TableRow>
              </TableHead>
              <TableBody>{items.map((it) => row(it))}</TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

function VisitorsLog() {
  const t = useT();
  return (
    <LogShell<VisitorScan>
      load={() => listScans(100)}
      empty={t('ops.emptyVisitors')}
      head={
        <>
          <TableCell>{t('ops.colWhen')}</TableCell>
          <TableCell>{t('ops.colVisitor')}</TableCell>
          <TableCell>{t('ops.colHost')}</TableCell>
          <TableCell>{t('ops.colScannedBy')}</TableCell>
        </>
      }
      row={(s) => (
        <TableRow key={s.id} hover>
          <TableCell>{fmt(s.scanned_at)}</TableCell>
          <TableCell>{s.visitor_name ?? '—'}</TableCell>
          <TableCell>{s.host_name ?? '—'}</TableCell>
          <TableCell>{s.scanned_by_name ?? '—'}</TableCell>
        </TableRow>
      )}
    />
  );
}

function PackagesLog() {
  const t = useT();
  return (
    <LogShell<PublicPackage>
      load={() => listOpsPackages(100)}
      empty={t('ops.emptyPackages')}
      head={
        <>
          <TableCell>{t('ops.colWhen')}</TableCell>
          <TableCell>{t('ops.colKind')}</TableCell>
          <TableCell>{t('ops.colRecipient')}</TableCell>
          <TableCell>{t('ops.colCarrier')}</TableCell>
          <TableCell>{t('common.status')}</TableCell>
        </>
      }
      row={(p) => (
        <TableRow key={p.id} hover>
          <TableCell>{fmt(p.created_at)}</TableCell>
          <TableCell>{p.unit_block && p.unit_number ? `${p.unit_block}/${p.unit_number}` : '—'}</TableCell>
          <TableCell>{p.recipient_name ?? '—'}</TableCell>
          <TableCell>{p.carrier ?? '—'}</TableCell>
          <TableCell>
            <Chip
              size="small"
              label={p.status === 'PICKED_UP' ? t('ops.pkgPickedUp') : p.status === 'CANCELLED' ? t('ops.pkgCancelled') : t('ops.pkgWaiting')}
              color={p.status === 'PICKED_UP' ? 'success' : p.status === 'CANCELLED' ? 'default' : 'warning'}
            />
          </TableCell>
        </TableRow>
      )}
    />
  );
}

function EmergencyLog() {
  const t = useT();
  return (
    <LogShell<PublicEmergencyAlert>
      load={() => listOpsEmergency(100)}
      empty={t('ops.emptyEmergency')}
      head={
        <>
          <TableCell>{t('ops.colWhen')}</TableCell>
          <TableCell>{t('ops.colKind')}</TableCell>
          <TableCell>{t('inv.colUnit')}</TableCell>
          <TableCell>{t('ops.colRaisedBy')}</TableCell>
          <TableCell>{t('common.status')}</TableCell>
        </>
      }
      row={(a) => (
        <TableRow key={a.id} hover>
          <TableCell>{fmt(a.created_at)}</TableCell>
          <TableCell>
            <Chip size="small" label={a.kind} color={a.kind === 'MEDICAL' || a.kind === 'FIRE' ? 'error' : 'warning'} variant="outlined" />
          </TableCell>
          <TableCell>{a.unit_block && a.unit_number ? `${a.unit_block}/${a.unit_number}` : '—'}</TableCell>
          <TableCell>{a.raised_by_name ?? '—'}</TableCell>
          <TableCell>
            <Chip
              size="small"
              label={a.status === 'RESOLVED' ? t('ops.alertResolved') : a.status === 'CANCELLED' ? t('ops.alertCancelled') : t('ops.alertActive')}
              color={a.status === 'RESOLVED' ? 'success' : a.status === 'CANCELLED' ? 'default' : 'error'}
            />
          </TableCell>
        </TableRow>
      )}
    />
  );
}
